import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import PhoneInput from 'components/PhoneInput';
import moment from 'moment';

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@ontruck/ontruck-react-components/lib/Modal';

import { Button, Field, Icon, Radios, Spacer, Tag, Textarea, Checkbox } from '@ontruck/orc';

import { Column, Row, Section, SectionTitle } from '../ModalFormLayout';

import CopyButton from '../CopyButton';
import CheckAndDateList from '../CheckAndDateList';
import { DRIVER_STATES, PREFERRED_LOCALES } from '../CarrierDetails/Constants';

import ReasonDeleteModal from 'components/ReasonDeleteModal';
import DocumentsList from '../DocumentsList';
import { getDriverDocuments } from './http';
import { Loading } from 'components/Loading';
import { isEmail } from 'utils/validators';
import Select from 'react-select-v3';
import { reactSelectStyles } from 'utils/style';
import { constants, getSpecificClientConfig } from 'redux/modules/client-config/selectors';
import { useSelector } from 'react-redux';

const adaptField = (field, value) => {
  switch (field) {
    case 'username':
      return (value || '').replace(/ /g, '');
    default:
      return value;
  }
};

const countryModifier = {
  UK: 'GB',
};

class DriverEditModal extends Component {
  static propTypes = {
    className: PropTypes.string,
    show: PropTypes.bool,
    driver: PropTypes.object,
    user: PropTypes.object,
    carrier: PropTypes.object,
    onClose: PropTypes.func,
    intl: PropTypes.any,
    onSave: PropTypes.func,
    onSaveSuccesfully: PropTypes.func,
    onResetPassword: PropTypes.func,
    allowStateUpdate: PropTypes.bool,
    remove: PropTypes.func,
  };

  constructor(props) {
    super(props);
    const { driver, user, isDocumentationEnabled } = props;
    const usernameType = user?.username?.includes('@') ? 'EMAIL' : 'PHONE';
    const logMethod = user ? usernameType : null;

    this.state = {
      originalUser: user,
      driver,
      user,
      newPassword: null,
      showUsernameAlert: false,
      usernameAlertRead: false,
      fieldErrors: [],
      submitting: false,
      showDeleteModal: false,
      server_errors: {},
      documents: [],
      documentsLoading: false,
      appsUser: logMethod,
      isDocumentationEnabled,
    };

    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.showFieldError = this.showFieldError.bind(this);
    this.handleUserFieldChange = this.handleUserFieldChange.bind(this);
    this.usernameHasChanged = this.usernameHasChanged.bind(this);
    this.handleServerSaveError = this.handleServerSaveError.bind(this);
    this.clearErrors = this.clearErrors.bind(this);
    this.validate = this.validate.bind(this);
    this.handleAppUserChange = this.handleAppUserChange.bind(this);
    this.getPhoneCustomFormat = this.getPhoneCustomFormat.bind(this);
    this.handleUsernameUpdate = this.handleUsernameUpdate.bind(this);
    this.getDocuments = this.getDocuments.bind(this);
  }

  componentDidMount() {
    if (this.state.driver?.id && this.state.isDocumentationEnabled) {
      this.getDocuments();
    }
  }

  showFieldError(field) {
    const { fieldErrors } = this.state;
    return fieldErrors.indexOf(field) !== -1;
  }

  handleAppUserChange(value) {
    const { user } = this.state;
    const usernamesMap = {
      EMAIL: user.email,
      PHONE: this.getPhoneCustomFormat(user.phone),
    };
    this.setState({
      user: {
        ...user,
        username: usernamesMap[value],
      },
      appsUser: value,
    });
  }

  usernameHasChanged() {
    const { originalUser = {}, user, usernameAlertRead, driver } = this.state;
    if (originalUser.username !== user.username && driver.status !== DRIVER_STATES.removed) {
      if (!usernameAlertRead) {
        this.setState({
          showUsernameAlert: true,
        });
      }
      return true;
    }

    return false;
  }

  async getDocuments() {
    const { driver } = this.state;
    try {
      this.setState({
        documentsLoading: true,
      });
      const documents = await getDriverDocuments(driver.id);
      this.setState({
        documents,
      });
    } catch (e) {
      console.error('Error getting documents', e);
    } finally {
      this.setState({
        documentsLoading: false,
      });
    }
  }

  clearErrors() {
    this.setState({
      fieldErrors: [],
    });
  }

  validate() {
    const { driver = {}, user = {} } = this.state;

    const validDate = (value) => !value || moment(value).isValid();

    const driver_validations = {
      drive_license: validDate,
      cap: validDate,
      adr: validDate,
      ban_reason: () => {
        if (driver.status === DRIVER_STATES.banned) {
          return driver.ban_reason && driver.ban_reason !== '';
        } else {
          return true;
        }
      },
    };

    const user_validations = {
      username: (value) => value && value !== '',
      first_name: (value) => value && value !== '',
      last_name: (value) => value && value !== '',
      phone: (value) => value && value !== '',
      email: (value) => (value ? isEmail(value) : true),
    };

    return new Promise((resolve, reject) => {
      const fieldErrors = Object.keys(driver_validations)
        .map((field) => (driver_validations[field](driver[field]) ? null : field))
        .filter((error) => error)
        .concat(
          Object.keys(user_validations)
            .map((field) => (user_validations[field](user[field]) ? null : field))
            .filter((error) => error)
        );

      if (fieldErrors.length === 0) {
        resolve();
      } else {
        this.setState(
          {
            fieldErrors,
          },
          () => reject(fieldErrors)
        );
      }
    });
  }

  async handleServerSaveError(err) {
    const response = err?.response;
    const errorObject = (await response?.json()) || err;
    const errorMap = {
      tax_number: 'tax_number_exist',
      username: 'username_exist',
      invalid_phone: 'invalid_phone',
      phone: 'phone',
      secondary_phone: 'secondary_phone',
    };
    let serverErrors = Object.keys(errorObject)
      .map((error) => (errorMap[error] ? errorMap[error] : null))
      .filter((n) => n); // AKA uniq

    const { fieldErrors } = this.state;
    if (serverErrors.length === 0) {
      serverErrors = ['error'];
    }

    this.setState({
      fieldErrors: [...fieldErrors, ...serverErrors],
      server_errors: err,
    });
  }

  handleSave() {
    const { onSave, onSaveSuccesfully } = this.props;
    if (!onSave) {
      return;
    }

    const { driver = {}, user, usernameAlertRead, originalUser } = this.state;

    this.clearErrors();

    this.validate()
      .then(() =>
        this.setState({ submitting: true }, () => {
          if (driver.id) {
            if (usernameAlertRead || !this.usernameHasChanged()) {
              onSave(driver, user, originalUser)
                .then(() => {
                  return onSaveSuccesfully();
                })
                .catch(this.handleServerSaveError)
                .then(() => this.setState({ submitting: false }));
            }
          } else {
            onSave(driver, user)
              .then((newDriver) => onSaveSuccesfully(newDriver))
              .catch(this.handleServerSaveError)
              .then(() => this.setState({ submitting: false }));
          }
        })
      )
      .catch((error) => console.error(error));
  }

  handleRemove() {
    const { remove } = this.props;

    if (!remove) {
      return;
    }

    const { driver } = this.state;

    remove({ id: driver.id });
  }

  handlePlusSignPhone(phone) {
    if (phone === '') return '';
    return phone[0] === '+' ? phone : `+${phone}`;
  }

  getPhoneCustomFormat(phone) {
    if (!phone) return phone;
    const normalizedPhone = phone[0] === '+' ? phone : `+${phone}`;
    return normalizedPhone.replace('+', '00');
  }

  getPreferredLocaleOption() {
    return PREFERRED_LOCALES.find(
      (pl) => pl.value.substr(0, 2) === this.state.user?.preferences?.language?.substr(0, 2)
    );
  }

  handleUsernameUpdate(field, value) {
    const { user, appsUser } = this.state;
    if (field === 'phone' && appsUser === 'PHONE') {
      return this.getPhoneCustomFormat(value);
    }
    if (field === 'email' && appsUser === 'EMAIL' && isEmail(value)) {
      return value;
    }
    return user?.username || '';
  }

  handleUserFieldChange(field, clearErrors = true) {
    return (value) => {
      const { user, fieldErrors } = this.state;
      this.setState({
        user: {
          ...user,
          [field]: adaptField(field, value),
          username: this.handleUsernameUpdate(field, value),
        },
        fieldErrors: clearErrors ? fieldErrors.filter((err) => err !== field) : fieldErrors,
      });
    };
  }

  handleFieldChange(field, clearErrors = true) {
    return (
      value,
      cb = () => {
        /*noop*/
      }
    ) => {
      const { driver, fieldErrors } = this.state;
      this.setState(
        {
          driver: {
            ...driver,
            [field]: value,
          },
          fieldErrors: clearErrors ? fieldErrors.filter((error) => error !== field) : fieldErrors,
        },
        typeof cb === 'function'
          ? cb
          : () => {
              /*noop*/
            }
      );
    };
  }

  render() {
    const {
      show,
      onClose,
      carrier,
      onResetPassword,
      intl,
      className,
      allowStateUpdate,
      isAddDriverLite,
    } = this.props;
    const {
      driver = {},
      user = {},
      newPassword,
      showUsernameAlert,
      submitting,
      documents,
      documentsLoading,
      appsUser,
    } = this.state;
    const t = intl.messages.components.DriverEditModal;
    const dateList = ['cap', 'adr'];

    return (
      <div data-testid="DriverEditModal">
        <Modal show={show} size="L" onClose={onClose} className={className}>
          <div className="driver-edit-modal">
            <ModalHeader>{driver.id ? t.titles.modal_edit : t.titles.modal_new}</ModalHeader>
            <ModalBody noPadding light>
              {!!driver.status && (
                <Section className="driver-edit-modal__section driver-edit-modal__section--dark">
                  <SectionTitle className="driver-edit-modal__title">
                    {t.titles.driver}
                  </SectionTitle>
                  <Row>
                    <Column half>
                      {(driver.status === DRIVER_STATES.active ||
                        driver.status === DRIVER_STATES.banned) && (
                        <Radios
                          girded
                          size="M"
                          name="status"
                          value={driver.status}
                          items={[
                            { value: DRIVER_STATES.active, label: t.active },
                            { value: DRIVER_STATES.banned, label: t.banned },
                          ]}
                          onChange={(e, { name, value }) => {
                            this.handleFieldChange('status')(value);
                          }}
                          disabled={!allowStateUpdate}
                        />
                      )}
                      {driver.status === DRIVER_STATES.not_active && (
                        <Tag
                          className="driver-edit-modal__tag"
                          text={t.not_activated}
                          skin="modernGrey"
                          weight="bold"
                          size="M"
                        />
                      )}
                      {driver.status === DRIVER_STATES.banned && (
                        <Textarea
                          className="driver-edit-modal__banned-field"
                          onChange={(e, { value }) => this.handleFieldChange('ban_reason')(value)}
                          name="ban_reason"
                          id="ban_reason"
                          label={t.labels.ban_reason}
                          value={driver.ban_reason}
                          height={'55px'}
                          error={this.showFieldError('ban_reason') || null}
                        />
                      )}
                    </Column>
                    {driver.id && user.id && (
                      <Column half>
                        <div className="driver-edit-modal__password-container">
                          {!newPassword ? (
                            <Button
                              ghost
                              skin="primary"
                              onClick={() =>
                                onResetPassword(user.id).then((newPassword) =>
                                  this.setState({ newPassword })
                                )
                              }
                              className="driver-edit-modal__password-button"
                              text={t.password_generate}
                            />
                          ) : (
                            <div className="driver-edit-modal__password-generated">
                              <span className="driver-edit-modal__password">{newPassword}</span>
                              <CopyButton
                                getText={() => Promise.resolve(newPassword)}
                                feedbackSuccess={t.password_copied}
                                small
                                className="driver-edit-modal__copy-button"
                              >
                                {t.password_copy}
                              </CopyButton>
                            </div>
                          )}
                        </div>
                      </Column>
                    )}
                  </Row>
                </Section>
              )}
              <Section className="driver-edit-modal__section">
                <SectionTitle className="driver-edit-modal__title">{t.titles.info}</SectionTitle>
                <Row>
                  <Column third>
                    <Field
                      id="first-name"
                      className="driver-edit-modal__field"
                      label={t.labels.first_name}
                      value={user.first_name || ''}
                      name="first-name"
                      onChange={(e, { value }) => this.handleUserFieldChange('first_name')(value)}
                      error={(this.showFieldError('first_name') && t.errors.first_name) || null}
                    />
                  </Column>
                  <Column third>
                    <Field
                      className="driver-edit-modal__field"
                      label={t.labels.last_name}
                      value={user.last_name || ''}
                      name="last-name"
                      id="last-name"
                      onChange={(e, { value }) => this.handleUserFieldChange('last_name')(value)}
                      error={(this.showFieldError('last_name') && t.errors.last_name) || null}
                    />
                  </Column>
                  <Column third>
                    <Field
                      className="driver-edit-modal__field"
                      label={t.labels.tax_mumber}
                      value={driver.tax_number || ''}
                      name="tax-number"
                      id="tax-number"
                      onChange={(e, { value }) =>
                        this.handleFieldChange('tax_number')((value || '').toUpperCase())
                      }
                      error={
                        (this.showFieldError('tax_number_exist') && t.errors.tax_number) || null
                      }
                    />
                  </Column>
                </Row>
                <Row>
                  <Column third>
                    <PhoneInput
                      error={
                        this.showFieldError('phone') ||
                        (this.showFieldError('username_exist') &&
                          this.state.appsUser === 'PHONE') ||
                        null
                      }
                      id="phone"
                      label={t.labels.phone}
                      country={countryModifier[user.country] || user.country}
                      value={user.phone || ''}
                      onChange={(value) => {
                        const formattedPhone = this.handlePlusSignPhone(value);
                        this.handleUserFieldChange('phone')(formattedPhone);
                      }}
                    />
                  </Column>
                  <Column third>
                    <PhoneInput
                      id="secondary-phone"
                      label={t.labels.secondary_phone}
                      country={countryModifier[user.country] || user.country}
                      value={driver.secondary_phone || ''}
                      onChange={(value) => {
                        const formattedPhone = this.handlePlusSignPhone(value);
                        this.handleFieldChange('secondary_phone')(formattedPhone);
                      }}
                      countryCodeEditable={true}
                      error={this.showFieldError('secondary_phone') || null}
                    />
                  </Column>
                  <Column third>
                    <Field
                      className="driver-edit-modal__field"
                      label={t.labels.email}
                      value={user.email || ''}
                      name="email"
                      id="email"
                      onChange={(e, { value }) => this.handleUserFieldChange('email')(value)}
                      error={
                        (this.showFieldError('username_exist') &&
                          this.state.appsUser === 'EMAIL') ||
                        this.showFieldError('username') ||
                        null
                      }
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Radios
                      girded
                      size="M"
                      name="dispatchee"
                      value={'dispatchee' in driver ? driver.dispatchee.toString() : 'false'}
                      items={[
                        { value: 'false', label: t.labels.transport_manager },
                        { value: 'true', label: t.labels.driver },
                      ]}
                      onChange={(e, { name, value }) => this.handleFieldChange('dispatchee')(value)}
                    />
                  </Column>
                </Row>
              </Section>
              {!isAddDriverLite && (
                <Section className="driver-edit-modal__section">
                  <SectionTitle className="driver-edit-modal__title">
                    {t.titles.fleet_service}
                  </SectionTitle>
                  <Row>
                    <Column>
                      <Checkbox
                        name="is_fleet_service"
                        label={t.labels.is_fleet_service}
                        girded
                        checked={driver?.is_fleet_service}
                        onChange={(_e, { name, value }) => {
                          this.handleFieldChange(name)(value);
                        }}
                      />
                    </Column>
                  </Row>
                </Section>
              )}
              <Section className="driver-edit-modal__section">
                <SectionTitle className="driver-edit-modal__title">
                  {t.titles.appsUser}
                </SectionTitle>
                <Row>
                  <Column>
                    <Radios
                      girded
                      size="M"
                      name="appsUser"
                      value={appsUser}
                      items={[
                        { value: 'EMAIL', label: t.labels.userMail },
                        { value: 'PHONE', label: t.labels.usePhone },
                      ]}
                      onChange={(e, { name, value }) => {
                        this.handleAppUserChange(value);
                      }}
                    />
                  </Column>
                  <Column>
                    <Field
                      className="driver-edit-modal__field"
                      label={t.labels.userApp}
                      value={user.username || ''}
                      name="userApp"
                      id="userApp"
                      disabled={true}
                      onChange={() => {
                        /*noop*/
                      }}
                    />
                  </Column>
                </Row>
              </Section>

              <Section className="driver-edit-modal__section">
                <SectionTitle className="driver-edit-modal__title">
                  {t.titles.language}
                </SectionTitle>
                <Row>
                  <Column third>
                    <Select
                      containerWidth="auto"
                      placeholder={t.labels.preferred_locale}
                      name="preferred_locale"
                      value={this.getPreferredLocaleOption()}
                      options={PREFERRED_LOCALES}
                      onChange={(selectedOption) =>
                        this.handleUserFieldChange('preferences')({
                          ...user?.preferences,
                          language: selectedOption.value,
                        })
                      }
                      styles={reactSelectStyles}
                    />
                  </Column>
                  <Column third />
                  <Column third />
                </Row>
              </Section>

              {!isAddDriverLite && (
                <Section className="driver-edit-modal__section">
                  <SectionTitle className="driver-edit-modal__title">{t.titles.docs}</SectionTitle>
                  <Row>
                    <Column>
                      {documentsLoading ? (
                        <Spacer mBottom="S">
                          <Loading />
                        </Spacer>
                      ) : (
                        <DocumentsList
                          serverDocuments={documents}
                          entity={{
                            entity_id: driver.id,
                            entity_type: 'driver',
                            entity_uuid: driver.uuid,
                          }}
                          refreshDocuments={this.getDocuments}
                          landingLink={carrier.landing_link}
                        />
                      )}
                    </Column>
                  </Row>
                  {driver.id ? (
                    <Row>
                      <Column half>
                        {carrier && carrier.google_drive_link ? (
                          <Button
                            nude
                            skin="primary"
                            href={carrier.google_drive_link}
                            link
                            className={'driver-edit-modal__doc-link'}
                            target="_blank"
                          >
                            {t.labels.google_drive_link}
                          </Button>
                        ) : null}
                      </Column>
                    </Row>
                  ) : null}
                  <Row>
                    <Column half>
                      <CheckAndDateList
                        fieldList={dateList}
                        object={driver}
                        t={t.labels}
                        size="M"
                        inputClass="driver-edit-modal__input driver-edit-modal__input--inline"
                        handleFieldChange={this.handleFieldChange}
                        checkboxClassname="driver-edit-modal__doc-checkbox"
                        errors={Object.assign(
                          ...dateList.map((name) =>
                            this.showFieldError(name) ? { [name]: t.errors.date } : {}
                          )
                        )}
                      />
                    </Column>
                  </Row>
                </Section>
              )}
              {!isAddDriverLite && (
                <Section className="driver-edit-modal__section">
                  <SectionTitle className="driver-edit-modal__title">{t.titles.uuid}</SectionTitle>
                  <Row>
                    <Column>{driver.uuid}</Column>
                  </Row>
                </Section>
              )}
            </ModalBody>
            <ModalFooter light>
              <div className="driver-edit-modal__footer-actions">
                {this.state.fieldErrors.length > 0 && (
                  <div className={'driver-edit-modal__general-errors'}>
                    {this.state.fieldErrors.map((error, index) => {
                      return (
                        <div key={`error-${index}`} className="driver-edit-modal__error">
                          {error.status && (
                            <span>
                              <strong>{error.status.code}</strong>: {error.status.message}
                            </span>
                          )}
                          {!error.status && (
                            <span>
                              <strong>{error}</strong>:{' '}
                              {t.errors[error] ? t.errors[error] : t.errors.required_field}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {driver.id && (
                  <span className="driver-edit-modal__delete-btn">
                    <Button
                      data-testid="carrier-driver-edit-modal__button-modal"
                      nude
                      text={t.delete}
                      onClick={() => this.setState({ showDeleteModal: true })}
                    />
                  </span>
                )}
                <Button
                  data-testid="driver-edit-modal__submit"
                  onClick={this.handleSave}
                  disabled={submitting}
                >
                  {driver.id ? t.save_edit : t.save_new}
                </Button>
              </div>
            </ModalFooter>
          </div>
        </Modal>
        <Modal
          show={showUsernameAlert}
          className={className}
          size="L"
          onClose={() =>
            this.setState({
              showUsernameAlert: false,
            })
          }
        >
          <div className="driver-edit-modal">
            <ModalHeader>{t.username_alert_title}</ModalHeader>
            <ModalBody light>
              <div className="driver-edit-modal__alert-body">
                <Icon
                  glyph="girded-exclamation"
                  name="alert"
                  className={'driver-edit-modal__alert-icon'}
                />
                <h3 className={'driver-edit-modal__alert-title'}>{t.username_alert_subtitle}</h3>
                <p>
                  {t.username_alert_text_1}
                  <br /> {driver.contact_name} {t.username_alert_text_2}
                </p>
                <p>
                  {t.username_alert_text_3} <strong>{user.username}</strong>{' '}
                  {t.username_alert_text_4}
                </p>
              </div>
            </ModalBody>
            <ModalFooter light>
              <div className={'driver-edit-modal__alert-footer'}>
                <div>
                  <Button
                    skin="secondary"
                    className="driver-edit-modal__action-button"
                    leftIcon="close"
                    onClick={() =>
                      this.setState({
                        showUsernameAlert: false,
                        usernameAlertRead: false,
                        submitting: false,
                      })
                    }
                  >
                    {t.username_alert_cancel}
                  </Button>
                </div>
                <div>
                  <Button
                    data-testid="driver-edit-modal__confirm-user-change"
                    skin="primary"
                    className="driver-edit-modal__action-button"
                    leftIcon="check"
                    onClick={() => {
                      this.setState(
                        {
                          showUsernameAlert: false,
                          usernameAlertRead: true,
                        },
                        this.handleSave
                      );
                    }}
                  >
                    {t.username_alert_save}
                  </Button>
                </div>
              </div>
            </ModalFooter>
          </div>
        </Modal>
        <ReasonDeleteModal
          show={this.state.showDeleteModal}
          onClose={() => this.setState({ showDeleteModal: false })}
          disabled={submitting}
          onSave={(reason) => {
            this.setState(
              {
                removeDriver: {
                  ...this.state.driver,
                  remove_reason: reason,
                  status: DRIVER_STATES.removed,
                },
                user: {
                  ...this.state.user,
                  username: `${this.state.user.id}-${this.state.user.username}`,
                },
              },
              this.handleRemove
            );
          }}
        />
      </div>
    );
  }
}

const HookHOCWrapper = (props) => {
  //allow use of hooks inside classes
  const isDocumentationEnabled = useSelector((state) =>
    getSpecificClientConfig(state, constants.DOCS_TAB)
  );

  props = {
    ...props,
    isDocumentationEnabled,
  };

  return <DriverEditModal {...props} />;
};

export default injectIntl(HookHOCWrapper);
